import { useId } from "react";
import type { ReactNode } from "react";

import { LoadingScreen } from "../lib/Loading.tsx";
import { Tag } from "../lib/Tag.tsx";
import { ENTITIES_COPY, ENTITY_TYPE_OPTIONS } from "./entities.constants.ts";
import type { EntityRow, HoldingRow, EntityTotals } from "./entities.model.ts";
import { useEntitiesController } from "./useEntitiesController.ts";

import "./entities.css";

/**
 * All the entities you act for, and everything they hold, in one place.
 *
 * OND-52 part (b). The toggles are the walkthrough's own word — "you can toggle
 * those on and off" — and they change nothing but the totals and the list below
 * them: turning an entity off does not end a seat, hide a collection from
 * anybody else, or alter a single row. It is a reading control.
 *
 * The create form is part (a) of the same ticket and sits at the foot rather
 * than the head, because the common reason to open this screen is to read the
 * aggregate and the rare one is to make a fourth owner.
 */
export function Entities() {
  const { view, form, setName, setOrgType, submitEntity, toggleEntity } =
    useEntitiesController();
  const nameId = useId();
  const typeId = useId();

  return (
    <div className="entities">
      <p className="entities__intro">{ENTITIES_COPY.intro}</p>

      {view.kind === "loading" && <LoadingScreen message={ENTITIES_COPY.loading} />}

      {view.kind === "error" && (
        <p className="entities__error" role="alert">
          {view.message}
        </p>
      )}

      {view.kind === "ready" && (
        <>
          <Totals totals={view.totals} nothingCounted={view.nothingCounted} />

          <Section title={ENTITIES_COPY.entitiesHeading} note={ENTITIES_COPY.entitiesNote}>
            <ul className="entities__rows">
              {view.entities.map((entity) => (
                <EntityLine
                  entity={entity}
                  key={entity.ondoId}
                  onToggle={() => {
                    toggleEntity(entity.ondoId);
                  }}
                />
              ))}
            </ul>
          </Section>

          <Section title={ENTITIES_COPY.holdingsHeading} note={ENTITIES_COPY.holdingsNote}>
            {view.holdings.length === 0 ? (
              <p className="entities__empty">
                {view.nothingCounted
                  ? ENTITIES_COPY.noEntitiesCounted
                  : ENTITIES_COPY.holdingsEmpty}
              </p>
            ) : (
              <ul className="entities__rows">
                {view.holdings.map((holding) => (
                  <HoldingLine holding={holding} key={holding.id} />
                ))}
              </ul>
            )}
          </Section>
        </>
      )}

      <Section title={ENTITIES_COPY.createHeading} note={ENTITIES_COPY.createNote}>
        <form
          className="entities__form"
          onSubmit={(event) => {
            event.preventDefault();
            submitEntity();
          }}
        >
          <label className="entities__field" htmlFor={nameId}>
            <span className="entities__label">{ENTITIES_COPY.nameLabel}</span>
            <input
              id={nameId}
              onChange={(event) => {
                setName(event.target.value);
              }}
              placeholder={ENTITIES_COPY.namePlaceholder}
              type="text"
              value={form.name}
            />
          </label>

          <label className="entities__field" htmlFor={typeId}>
            <span className="entities__label">{ENTITIES_COPY.typeLabel}</span>
            <select
              id={typeId}
              // The chosen VALUE looked up in the list the options came from,
              // rather than cast out of the event. A cast would let a tampered
              // `<option>` reach the request body as an org type nothing offered;
              // a lookup cannot produce a value that is not in
              // `ENTITY_ORG_TYPES`, which is the same list the server refuses
              // everything outside of.
              onChange={(event) => {
                const chosen = ENTITY_TYPE_OPTIONS.find(
                  (option) => option.value === event.target.value,
                );
                if (chosen) setOrgType(chosen.value);
              }}
              value={form.orgType}
            >
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <p className="entities__hint">
            {ENTITY_TYPE_OPTIONS.find((option) => option.value === form.orgType)?.hint}
          </p>

          <button disabled={form.busy} type="submit">
            {form.busy ? ENTITIES_COPY.creating : ENTITIES_COPY.createAction}
          </button>

          <p className="entities__limits">{ENTITIES_COPY.createLimits}</p>

          {form.error !== null && (
            <p className="entities__error" role="alert">
              {form.error}
            </p>
          )}
          {form.created !== null && (
            <p className="entities__created" role="status">
              {form.created}
            </p>
          )}
        </form>
      </Section>
    </div>
  );
}

/**
 * A titled box, named by its own heading.
 *
 * Not `profile/ProfilePanels.tsx`'s `Panel`, which carries `profile__` classes
 * and imports that screen's model — borrowing it would couple two screens for
 * a border. The `aria-labelledby` is the part worth copying, and is: a bare
 * `<section>` is exposed as no region at all.
 */
function Section({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}) {
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="entities__panel">
      <h2 className="entities__panel-title" id={titleId}>
        {title}
      </h2>
      <p className="entities__panel-note">{note}</p>
      {children}
    </section>
  );
}

function Totals({
  totals,
  nothingCounted,
}: {
  totals: EntityTotals;
  nothingCounted: boolean;
}) {
  return (
    <p className="entities__totals" role="status">
      {nothingCounted ? (
        ENTITIES_COPY.noEntitiesCounted
      ) : (
        <>
          <strong>{totals.works}</strong> {ENTITIES_COPY.totalWorks} ·{" "}
          <strong>{totals.collections}</strong> {ENTITIES_COPY.totalCollections} ·{" "}
          <strong>{totals.entities}</strong> {ENTITIES_COPY.totalEntities}
        </>
      )}
    </p>
  );
}

/**
 * One entity, with the switch that counts it.
 *
 * A CHECKBOX AND NOT A BUTTON. "Counted in the totals" is a state the row is
 * in, which is what a checkbox says and what a toggle button only implies — and
 * it is the control a screen reader announces as checked or not.
 */
function EntityLine({ entity, onToggle }: { entity: EntityRow; onToggle: () => void }) {
  const toggleId = useId();

  return (
    <li className={`entities__row${entity.counted ? "" : " entities__row--off"}`}>
      <input
        checked={entity.counted}
        className="entities__toggle"
        id={toggleId}
        onChange={onToggle}
        type="checkbox"
      />
      <label className="entities__row-body" htmlFor={toggleId}>
        <span className="entities__row-title">{entity.name}</span>
        <span className="entities__row-meta">
          {[entity.typeLabel, entity.levelLabel, entity.ondoId]
            .filter((part) => part !== null)
            .join(" · ")}
        </span>
      </label>
      <span className="entities__row-counts">
        {entity.workCount} {ENTITIES_COPY.totalWorks} · {entity.collectionCount}{" "}
        {ENTITIES_COPY.totalCollections}
      </span>
      <Tag tone={entity.counted ? "answer" : "quiet"}>
        {entity.counted ? ENTITIES_COPY.shown : ENTITIES_COPY.hidden}
      </Tag>
    </li>
  );
}

/** One collection, with the entity that holds it named beside it. */
function HoldingLine({ holding }: { holding: HoldingRow }) {
  return (
    <li className="entities__row">
      <div className="entities__row-body">
        <span className="entities__row-title">{holding.name}</span>
        <span className="entities__row-meta">
          {holding.workCount} {ENTITIES_COPY.totalWorks}
        </span>
      </div>
      <Tag tone={holding.owner.kind === "undisclosed" ? "quiet" : "neutral"}>
        {holding.owner.label}
      </Tag>
    </li>
  );
}
